import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { COAForm } from '../../components/coa/COAForm';
import { COAFormData } from '../../types/coa.types';
import { coaService } from '../../services/coa.service';
export function COAFormPage() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const isEdit = !!id;
  const [initialData, setInitialData] = useState<COAFormData | undefined>();
  const [loading, setLoading] = useState(isEdit);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingData, setPendingData] = useState<COAFormData | null>(null);
  useEffect(() => {
    if (isEdit && id) {
      loadAccount(id);
    }
  }, [id, isEdit]);
  const loadAccount = async (accountId: string) => {
    try {
      const account = await coaService.getAccountById(accountId);
      if (account) {
        setInitialData({
          accountCode: account.accountCode,
          accountName: account.accountName,
          accountNameSomali: account.accountNameSomali,
          parentId: account.parentId,
          accountType: account.accountType,
          subType: account.subType,
          openingDate: account.openingDate,
          branchId: account.branchId,
          projectId: account.projectId,
          description: account.description,
          additionalNote: account.additionalNote,
          isInactive: account.isInactive,
          bankNameId: account.bankNameId,
          bankBranchId: account.bankBranchId,
          bankAccountNo: account.bankAccountNo,
          prefix: account.prefix
        });
      }
    } catch (error) {
      console.error('Failed to load account:', error);
    } finally {
      setLoading(false);
    }
  };
  const handleSubmit = (data: COAFormData) => {
    setPendingData(data);
    setShowConfirmModal(true);
  };
  const handleConfirm = async () => {
    if (!pendingData) return;
    try {
      if (isEdit && id) {
        await coaService.updateAccount(id, pendingData);
      } else {
        await coaService.createAccount(pendingData);
      }
      navigate('/coa');
    } catch (error: any) {
      alert(error.message || 'Failed to save account');
    } finally {
      setShowConfirmModal(false);
    }
  };
  if (loading) {
    return <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center" data-id="element-1068">
        <div className="text-gray-500" data-id="element-1069">Loading account...</div>
      </div>;
  }
  return <div className="min-h-screen bg-gray-50 p-6" data-id="element-1070">
      <div className="max-w-4xl mx-auto space-y-6" data-id="element-1071">
        {/* Header */}
        <div className="flex items-center gap-4" data-id="element-1072">
          <Button variant="ghost" onClick={() => navigate('/coa')} data-id="element-1073">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-1074" />
          </Button>
          <div data-id="element-1075">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1076">
              {isEdit ? 'Edit Account' : 'Create New Account'}
            </h1>
            <p className="text-sm text-gray-600 mt-1" data-id="element-1077">
              {isEdit ? 'Update account details' : 'Add a new account to your chart of accounts'}
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg border p-6" data-id="element-1078">
          <COAForm initialData={initialData} onSubmit={handleSubmit} onCancel={() => navigate('/coa')} isEdit={isEdit} data-id="element-1079" />
        </div>
      </div>

      {/* Confirmation Modal */}
      <Modal isOpen={showConfirmModal} onClose={() => setShowConfirmModal(false)} title={isEdit ? 'Confirm Update' : 'Confirm Creation'} footer={<>
            <Button variant="secondary" onClick={() => setShowConfirmModal(false)} data-id="element-1081">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirm} data-id="element-1082">
              Confirm
            </Button>
          </>} data-id="element-1080">
        <p className="text-gray-700" data-id="element-1083">
          {isEdit ? 'Are you sure you want to update this account?' : 'Are you sure you want to create this account?'}
        </p>
        {pendingData && <div className="mt-4 p-3 bg-gray-50 rounded text-sm" data-id="element-1084">
            <div data-id="element-1085">
              <strong data-id="element-1086">Code:</strong> {pendingData.accountCode}
            </div>
            <div data-id="element-1087">
              <strong data-id="element-1088">Name:</strong> {pendingData.accountName}
            </div>
            <div data-id="element-1089">
              <strong data-id="element-1090">Type:</strong> {pendingData.accountType}
            </div>
          </div>}
      </Modal>
    </div>;
}